import React, { useState } from 'react';
import { Menu, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_autoposter-32/artifacts/j0h273d6_auto.png";
const APP_STORE_URL = "https://apps.apple.com/app/id6762516648";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.autopostr.app";
const APP_STORE_BADGE = "https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83";
const PLAY_STORE_BADGE = "https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png";

// Compact App Store + Play Store badges - exported for reuse on dashboard
export const AppDownloadBadges = ({ compact = false }) => (
  <div className={`flex items-center ${compact ? 'gap-1.5' : 'gap-2'}`} data-testid="app-download-badges">
    <a
      href={APP_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="header-app-store-badge"
      aria-label="Download on the App Store"
      className="inline-block hover:opacity-80 transition-opacity"
    >
      <img
        src={APP_STORE_BADGE}
        alt="Download on the App Store"
        className={compact ? 'h-7 w-auto' : 'h-9 w-auto'}
      />
    </a>
    <a
      href={PLAY_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="header-play-store-badge"
      aria-label="Get it on Google Play"
      className="inline-block hover:opacity-80 transition-opacity"
    >
      <img
        src={PLAY_STORE_BADGE}
        alt="Get it on Google Play"
        className={compact ? 'h-10 w-auto -my-1.5' : 'h-12 w-auto -my-1.5'}
      />
    </a>
  </div>
);

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAuthenticated, setShowAuthModal } = useAuth();

  const handleGetStarted = (e) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    if (isAuthenticated) {
      window.location.href = '/dashboard';
    } else {
      setShowAuthModal('signup');
    }
  };

  // Full navigation as requested
  const navLinks = [
    { label: 'How It Works', href: '/#how-it-works' },
    { label: 'Content Library', href: '/#content-library' },
    { label: 'Theme Pages', href: '/themepages' },
    { label: 'Pricing', href: '/pricing' },
    { label: 'Blog', href: '/blog' },
    { label: 'FAQ', href: '/#faq' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 px-4 pt-4 md:px-6">
      <nav className="max-w-7xl mx-auto flex items-center justify-between px-4 md:px-6 h-14 bg-[#0a0a0a]/80 backdrop-blur-xl border border-white/10 rounded-full">
        {/* Logo */}
        <a href="/" className="flex items-center flex-shrink-0">
          <img
            src={LOGO_URL}
            alt="AutoPostr"
            className="h-10 md:h-12 w-auto"
          />
        </a>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              className="px-3 py-2 text-[#737373] hover:text-white hover:bg-white/5 rounded-full transition-all duration-200 text-sm font-medium"
            >
              {link.label}
            </a>
          ))}
        </div>

        {/* Right side - Get Started CTA + mobile menu */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleGetStarted}
            data-testid="header-get-started-btn"
            className="hidden md:inline-flex btn-primary text-sm py-2.5 px-5"
          >
            Get Started
          </button>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 hover:bg-white/5 rounded-full transition-colors text-white"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {/* App Store + Google Play badges - positioned UNDER the nav bar, top-right */}
      <div className="hidden md:flex justify-end max-w-7xl mx-auto mt-2 pr-4">
        <div className="bg-[#0a0a0a]/60 backdrop-blur-md border border-white/10 rounded-full px-3 py-1.5">
          <AppDownloadBadges compact />
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden mt-2 mx-4 p-4 bg-[#141414]/95 backdrop-blur-xl border border-white/10 rounded-2xl">
          <div className="flex flex-col gap-2">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-[#a3a3a3] hover:text-white hover:bg-white/5 rounded-xl transition-all duration-200 font-medium"
              >
                {link.label}
              </a>
            ))}
            <button
              onClick={handleGetStarted}
              data-testid="header-mobile-get-started-btn"
              className="btn-primary mt-2 text-center"
            >
              Get Started
            </button>

            {/* App badges under Get Started in mobile menu */}
            <div className="mt-3 pt-3 border-t border-white/10 flex flex-col items-center gap-2">
              <p className="text-white/60 text-xs">Get the AutoPostr app</p>
              <AppDownloadBadges />
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
